import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
    ChefHat,
    LayoutDashboard,
    Store,
    BarChart3,
    LogOut,
    Menu,
    X,
    Plus,
    UserCog,
    Gem,
    Sparkles
} from 'lucide-react';

const restaurantNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['super_admin', 'admin', 'shop_admin'] },
    { path: '/dashboard/restaurants', label: 'Restaurants', icon: Store, roles: ['super_admin', 'admin', 'shop_admin'] },
    { path: '/dashboard/shop-admins', label: 'Shop Admins', icon: UserCog, roles: ['super_admin', 'admin'] },
    { path: '/dashboard/analytics', label: 'Analytics', icon: BarChart3, roles: ['super_admin', 'admin', 'shop_admin'] },
];

const perfumeNavItems = [
    { path: '/dashboard/perfume', label: 'Dashboard', icon: LayoutDashboard, roles: ['super_admin', 'admin', 'shop_admin'] },
    { path: '/dashboard/perfume/shops', label: 'Perfume Shops', icon: Gem, roles: ['super_admin', 'admin', 'shop_admin'] },
    { path: '/dashboard/shop-admins', label: 'Shop Admins', icon: UserCog, roles: ['super_admin', 'admin'] },
];

export default function DashboardLayout({ children }) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const isSuperAdmin = ['super_admin', 'admin'].includes(user?.role);
    const isPerfumeRoute = location.pathname.startsWith('/dashboard/perfume');
    const [activeCategory, setActiveCategory] = useState(isPerfumeRoute ? 'perfume' : 'restaurant');

    const navItems = activeCategory === 'perfume' ? perfumeNavItems : restaurantNavItems;

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const switchCategory = (cat) => {
        setActiveCategory(cat);
        setSidebarOpen(false);
        if (cat === 'perfume') navigate('/dashboard/perfume');
        else navigate('/dashboard');
    };

    return (
        <div className="min-h-screen bg-[#F5F3E9]">
            {/* Mobile Header */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#E5E1D1] px-4 py-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${activeCategory === 'perfume' ? 'bg-[#1A1A2E]' : 'bg-[#C75D4E]'}`}>
                            {activeCategory === 'perfume' ? <Sparkles className="w-5 h-5 text-white" /> : <ChefHat className="w-5 h-5 text-white" />}
                        </div>
                        <span className="font-heading text-xl font-medium text-[#2B2A27]">
                            {activeCategory === 'perfume' ? 'Perfume Studio' : 'AR Menu Studio'}
                        </span>
                    </div>
                    <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 text-[#2B2A27]" data-testid="mobile-menu-toggle">
                        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </header>

            {/* Sidebar */}
            <aside className={`fixed top-0 left-0 z-40 h-screen w-72 border-r border-[#E5E1D1] transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} ${activeCategory === 'perfume' ? 'bg-[#1A1A2E]' : 'bg-[#F0EDDF]'}`}>
                <div className="flex flex-col h-full p-6">
                    {/* Logo */}
                    <Link to={activeCategory === 'perfume' ? '/dashboard/perfume' : '/dashboard'} className="flex items-center gap-3 mb-6">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${activeCategory === 'perfume' ? 'bg-[#D4AF37]' : 'bg-[#C75D4E]'}`}>
                            {activeCategory === 'perfume' ? <Sparkles className="w-6 h-6 text-white" /> : <ChefHat className="w-6 h-6 text-white" />}
                        </div>
                        <div>
                            <span className={`font-heading text-xl font-medium block ${activeCategory === 'perfume' ? 'text-white' : 'text-[#2B2A27]'}`}>
                                {activeCategory === 'perfume' ? 'Perfume Studio' : 'AR Menu Studio'}
                            </span>
                            <span className={`text-xs ${activeCategory === 'perfume' ? 'text-white/40' : 'text-[#969282]'}`}>Admin Dashboard</span>
                        </div>
                    </Link>

                    {/* Category Switcher - only for super admin */}
                    {isSuperAdmin && (
                        <div className={`flex gap-1 p-1 rounded-xl mb-4 ${activeCategory === 'perfume' ? 'bg-white/5' : 'bg-[#E5E1D1]'}`} data-testid="category-switcher">
                            <button
                                onClick={() => switchCategory('restaurant')}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all ${
                                    activeCategory === 'restaurant'
                                        ? (activeCategory === 'perfume' ? 'bg-white/10 text-white' : 'bg-white text-[#C75D4E] shadow-sm')
                                        : (activeCategory === 'perfume' ? 'text-white/40 hover:text-white/60' : 'text-[#69665B] hover:bg-white/50')
                                }`}
                                data-testid="switch-restaurant"
                            >
                                <Store className="w-3.5 h-3.5" /> Restaurant
                            </button>
                            <button
                                onClick={() => switchCategory('perfume')}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all ${
                                    activeCategory === 'perfume'
                                        ? 'bg-[#D4AF37] text-white shadow-sm'
                                        : (activeCategory === 'perfume' ? 'text-white/40' : 'text-[#69665B] hover:bg-white/50')
                                }`}
                                data-testid="switch-perfume"
                            >
                                <Sparkles className="w-3.5 h-3.5" /> Perfume
                            </button>
                        </div>
                    )}

                    {/* Quick Action */}
                    {activeCategory === 'perfume' ? (
                        <Link to="/dashboard/perfume/shops/new" className="mb-6">
                            <Button className="w-full bg-[#D4AF37] hover:bg-[#C9A227] text-white rounded-full gap-2" data-testid="new-perfume-shop-button">
                                <Plus className="w-4 h-4" /> New Perfume Shop
                            </Button>
                        </Link>
                    ) : (
                        <Link to="/dashboard/restaurants/new" className="mb-6">
                            <Button className="w-full bg-[#C75D4E] hover:bg-[#A94E40] text-white rounded-full gap-2" data-testid="new-restaurant-button">
                                <Plus className="w-4 h-4" /> New Restaurant
                            </Button>
                        </Link>
                    )}

                    {/* Navigation */}
                    <nav className="flex-1 space-y-1">
                        {navItems
                            .filter(item => item.roles.includes(user?.role || 'admin'))
                            .map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.path ||
                                            (item.path !== '/dashboard' && item.path !== '/dashboard/perfume' && location.pathname.startsWith(item.path));
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setSidebarOpen(false)}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                                        isActive
                                            ? (activeCategory === 'perfume' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-white text-[#C75D4E] shadow-sm')
                                            : (activeCategory === 'perfume' ? 'text-white/50 hover:bg-white/5 hover:text-white/80' : 'text-[#69665B] hover:bg-white/50')
                                    }`}
                                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                    <Icon className="w-5 h-5" />
                                    <span className="font-medium">{item.label}</span>
                                </Link>
                            );
                        })}
                    </nav>

                    {/* User & Logout */}
                    <div className={`border-t pt-4 ${activeCategory === 'perfume' ? 'border-white/10' : 'border-[#E5E1D1]'}`}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-[#8DA399] rounded-full flex items-center justify-center text-white font-medium">
                                {user?.name?.charAt(0) || user?.email?.charAt(0) || 'A'}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${activeCategory === 'perfume' ? 'text-white' : 'text-[#2B2A27]'}`}>{user?.name || 'Admin'}</p>
                                <p className={`text-xs truncate ${activeCategory === 'perfume' ? 'text-white/40' : 'text-[#969282]'}`}>{user?.email}</p>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            onClick={handleLogout}
                            className={`w-full justify-start gap-3 ${activeCategory === 'perfume' ? 'text-white/50 hover:text-red-400 hover:bg-red-500/10' : 'text-[#69665B] hover:text-[#B84A4A] hover:bg-red-50'}`}
                            data-testid="logout-button"
                        >
                            <LogOut className="w-5 h-5" />
                            Sign out
                        </Button>
                    </div>
                </div>
            </aside>

            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
            )}

            {/* Main Content */}
            <main className="lg:ml-72 min-h-screen pt-16 lg:pt-0">
                <div className="p-6 lg:p-10">
                    {children}
                </div>
            </main>
        </div>
    );
}
